import { getName } from "country-list";
import type { CountryTabValue, ExportModalEntity } from "./types";
import type { CountryTab } from "./types";

const ALL_COUNTRIES_LABEL = "All Countries";

/**
 * Returns sorted unique country values from entities.
 * Entities with null/empty country are excluded from the list (they stay under "All Countries").
 * Pure function.
 */
export function getUniqueCountries(entities: ExportModalEntity[]): string[] {
  const countries = [
    ...new Set(
      entities
        .map((e) => e.country)
        .filter((c): c is string => c != null && String(c).trim() !== ""),
    ),
  ];
  return countries.sort((a, b) => a.localeCompare(b));
}

/**
 * Builds country tabs for the modal.
 * When there are 0 or 1 countries, returns a single "All Countries" tab (no country filter UI).
 * When there are 2+, returns "All Countries" plus one tab per country.
 * Pure function.
 */
export function getCountryTabs(entities: ExportModalEntity[]): {
  tab: "single" | "multiple";
  tabs: CountryTab[];
} {
  const countries = getUniqueCountries(entities);
  if (countries.length <= 1) {
    return {
      tab: "single",
      tabs: [{ value: "ALL", label: ALL_COUNTRIES_LABEL }],
    };
  }
  const countryTabs = countries
    .map((c): CountryTab => ({ value: c, label: getName(c) ?? c }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return {
    tab: "multiple",
    tabs: [{ value: "ALL", label: ALL_COUNTRIES_LABEL }, ...countryTabs],
  };
}
